import { Form, Field } from "react-final-form";
import { renderTextField } from "../../components/CustomReduxFormComponent";
import React, { useState, useEffect, useRef } from "react";
import SimpleControlDialog from "../../components/SimpleControlDialog";
import otpService from "../../services/otp.service";
import Enums from "../../utils/Enums";
import {
  Typography,
  Box
} from '@mui/material';

const typographyRootStyle = {
  color: "red",
  whiteSpace: "pre-line",
  textAlign: "center",
  margin: 0,
  fontFamily: "Pru Sans,Roboto,Helvetica,Arial,sans-serif",
  fontWeight: 400,
  fontSize: "0.75rem",
  lineHeight: "1.66"
};
const required = value => (value ? undefined : "Required");

// Shared variables to persist timer state across component instances
let sharedTimer = 30;
let sharedIsButtonDisabled = false;
let sharedTimerInterval = null;
let lastClosedTimestamp = null;
let resendOtpClicked = false;

const OtpDialog2 = ({ show, txId, description, closeHandler, submitHandler, submitError, submitErrorMessage, shouldSendOtp, switchToDigitalTokenHandler, totpStatus }) => {
  const [timer, setTimer] = useState(sharedTimer);
  const [isButtonDisabled, setIsButtonDisabled] = useState(sharedIsButtonDisabled);
  const [dialogDescription, setDialogDescription] = useState(description); // State for dynamic description
  const [isError, setIsError] = useState(false); // State to track if the message is an error

  const getErrorMessage = (errorText = "Invalid Login") => (
    <>
      {errorText}. Please contact the PRUONE Service Desk at 6732 3850 or email{" "}
      <a href="mailto:sgp.helpdesk@prudential.com.sg" style={{ color: "blue", textDecoration: "underline" }}>
        sgp.helpdesk@prudential.com.sg
      </a>{" "}
      for assistance.
    </>
  );

  useEffect(() => {
    const handleDialogOpen = async () => {
      if (show) {
        // Calculate the time that has lapsed while the dialog was closed
        if (resendOtpClicked && lastClosedTimestamp) {
          const elapsedTime = Math.floor((Date.now() - lastClosedTimestamp) / 1000);
          const remainingTime = Math.max(0, sharedTimer - elapsedTime);
          sharedTimer = remainingTime;
          setTimer(remainingTime);
          setIsButtonDisabled(remainingTime > 0);

          if (sharedTimer > 0) {
            startTimer(); // Restart the timer if there is remaining time
          }
        }

        // Call sendOtp when the dialog appears
        if (shouldSendOtp) {
          try {
            const response = await otpService.sendOtp(Enums.OTP_TYPE.EMAIL, txId, false); // Call sendOtp from otp.service.js
            console.log("OTP sent successfully:", response);
            setDialogDescription(response.data.message); // Reset to the original description on success
            setIsError(false); // Reset error state
          } catch (error) {
            console.error("Error sending OTP:", error);
            setDialogDescription(getErrorMessage("Invalid Login"));
            setIsError(true);
          }
        }

        else {
          setDialogDescription(getErrorMessage("Invalid Login"));
          setIsError(true);
        }
      }
    };

    handleDialogOpen();

    return () => {
      // Cleanup function to reset shared variables when the dialog is closed
      sharedTimer = timer;
      sharedIsButtonDisabled = isButtonDisabled;
      lastClosedTimestamp = Date.now();
      clearInterval(sharedTimerInterval);
      sharedTimerInterval = null;

    };
  }, [show]);

  const startTimer = () => {
    if (sharedTimerInterval) return; // Prevent multiple intervals
    setIsButtonDisabled(true);
    sharedIsButtonDisabled = true;

    sharedTimerInterval = setInterval(() => {
      setTimer(prevTimer => {
        if (prevTimer <= 1) {
          clearInterval(sharedTimerInterval);
          sharedTimerInterval = null;
          sharedTimer = 30;
          sharedIsButtonDisabled = false;
          resendOtpClicked = false; // Reset the flag when timer ends
          lastClosedTimestamp = null; // Reset the timestamp
          setTimer(sharedTimer);
          setIsButtonDisabled(sharedIsButtonDisabled);
          return 30;
        }
        sharedTimer = prevTimer - 1;
        return prevTimer - 1;
      });
    }, 1000);
  };

  const handleResendOtp = async () => {
    if (isButtonDisabled) return; // Prevent multiple clicks during countdown
    resendOtpClicked = true;
    sharedTimer = 30;
    setTimer(sharedTimer);
    startTimer();

    try {
      const response = await otpService.sendOtp(Enums.OTP_TYPE.EMAIL, txId, true); // Call sendOtp from otp.service.js
      console.log("OTP sent successfully:", response);
      setDialogDescription(response.data.message); // Reset to the original description on success
      setIsError(false); // Reset error state
    } catch (error) {
      console.error("Error resending OTP:", error);
      setDialogDescription(getErrorMessage("An error occurred while resending the OTP")); // Update description with error message and clickable email
      setIsError(true); // Set error state
    }
  };

  const submit = values => {
    submitHandler(values.otp, Enums.OTP_TYPE.EMAIL);
  };

  return (
    <>
      <Form
        onSubmit={submit}
        render={({ handleSubmit, form, valid }) => {
          return (
            <>
              <SimpleControlDialog
                show={show}
                title="Enter OTP"
                description={
                  <span style={isError ? { color: "red" } : {}}>
                    {dialogDescription}
                  </span>
                } // Conditionally apply red color for error messages
                onClose={closeHandler}
                closeHandler={handleResendOtp}
                valid={valid}
                onSubmit={handleSubmit}
                showCloseButton={true}
                okButtonText="Submit"
                closeButtonText={`Resend OTP (${timer})`}
                closeButtonDisabled={isButtonDisabled || isError}
                footerContent={
                  totpStatus === Enums.TOTP_STATUS.ENABLED &&
                  (
                    <Box display="flex" justifyContent="center" alignItems="center">
                      <Typography variant="body2" sx={{ color: 'black', fontWeight: 400 }}>
                        Having trouble?&nbsp;
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'black', textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={switchToDigitalTokenHandler}
                      >
                        Switch to Digital Token
                      </Typography>
                    </Box>
                  )
                }
              >
                <Field
                  name="otp"
                  autoFocus
                  margin="dense"
                  component={renderTextField}
                  autoComplete="[off]"
                  label="OTP"
                  type="text"
                  validate={required}
                />
                {submitError && (
                  <div variant="caption" style={typographyRootStyle}>
                    {submitErrorMessage}
                  </div>
                )}

              </SimpleControlDialog>
            </>
          );
        }
        }
      />
    </>
  );
};
export default OtpDialog2;